import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useHistory, useLocation } from 'react-router-dom';
import { useModal } from 'contexts/ModalContext/ModalContext';
import { RootState } from 'modules';
import apiClient from 'libs/api/apiClient';
import { ERoute } from 'enums/route.enum';
import styled from 'styled-components';
import { device } from 'GlobalStyles';
import CircleShapedBackButton from './CircleShapedBackButton';
import InterviewMasterClassBasicIntroductionModal from './modals/InterviewMasterClassBasicIntroductionModal';
import InterviewMasterClassBasicMentorsModal from './modals/InterviewMasterClassBasicMentorsModal';
import InterviewMasterClassBasicReviewsModal from './modals/InterviewMasterClassBasicReviewsModal';
import InterviewMasterClassBasicSelfStudyModal from './modals/InterviewMasterClassBasicSelfStudyModal';
import VideoLectureIndex from './VideoLectureIndex';
import { LectureChapter, LectureSession } from './VideoLectureIndex/VideoLectureIndexChapter';

declare global {
  interface Window {
    Vimeo: any;
  }
}

const LECTURE_NAME = '전략컨설팅 인터뷰 MasterClass [기초편]';

type Lecture = {
  id: number;
  title: string;
  chapters: LectureChapter[];
  myReview: null | { rating: number; content: string };
};

function InterviewMasterClassBasicPlayerPage() {
  const history = useHistory();
  const isMobile = useMediaQuery({ query: device.md });
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [lecture, setLecture] = useState<Lecture>();
  const [session, setSession] = useState<LectureSession | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentChapterOrder = Number(searchParams.get('chapter')) || 1;
  const currentSessionOrder = Number(searchParams.get('session')) || 1;
  const key = `${currentChapterOrder}-${currentSessionOrder}`;

  const updateLastViewedAt = () => {
    apiClient.head('/lectures/1');
  };

  const updateLecture = () => {
    apiClient.get('/lectures/1').then((res) => setLecture(res.data as Lecture));
  };

  useEffect(() => {
    updateLecture();
  }, []);

  useEffect(() => {
    apiClient
      .get(`/lectures/1/chapters/${currentChapterOrder}/sessions/${currentSessionOrder}`)
      .then((res) => setSession(res.data as LectureSession));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!session) return;
    const iframe = iframeRef.current;
    const player = new window.Vimeo.Player(iframe);

    player.on('play', updateLastViewedAt);
  }, [session]);

  if (currentUser && !currentUser.is_interview_master_class_basic_available) {
    history.replace(ERoute.LectureInterviewMasterClassBasic);
    return null;
  }

  return (
    <StyledContainer isMobile={isMobile}>
      <div className='left'>
        <header className='header'>
          <CircleShapedBackButton />
          <h1>{LECTURE_NAME}</h1>
        </header>

        <section className='main'>
          {/* 비디오 플레이어 */}
          <div className='player'>
            <iframe
              ref={iframeRef}
              src={
                session
                  ? `https://player.vimeo.com/video/${session.vimeoId}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`
                  : undefined
              }
              frameBorder='0'
              allow='autoplay; fullscreen; picture-in-picture; clipboard-write'
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              title={session?.title}
            />
          </div>

          {/* 푸터 */}
          {!isMobile && <Footer myReview={lecture?.myReview} updateLecture={updateLecture} />}
        </section>
      </div>

      <div className='right'>
        <header className='header'>강의 목차</header>
        <section className='lectures-list'>
          {lecture?.chapters && (
            <VideoLectureIndex
              chapters={lecture.chapters}
              currentChapterOrder={currentChapterOrder}
              currentSessionOrder={currentSessionOrder}
            />
          )}
        </section>
      </div>

      {isMobile && <Footer myReview={lecture?.myReview} updateLecture={updateLecture} />}
    </StyledContainer>
  );
}

function Footer({
  myReview,
  updateLecture,
}: {
  myReview: ComponentProps<typeof InterviewMasterClassBasicReviewsModal>['review'];
  updateLecture: () => void;
}) {
  const modal = useModal();
  return (
    <div className='footer'>
      <button
        className='footer-button'
        onClick={() => modal.open({ element: <InterviewMasterClassBasicIntroductionModal /> })}
      >
        <span>강의 소개</span>
      </button>
      <button
        className='footer-button'
        onClick={() => modal.open({ element: <InterviewMasterClassBasicMentorsModal /> })}
      >
        <span>멘토 소개</span>
      </button>
      <button
        className='footer-button'
        onClick={() =>
          modal.open({
            element: (
              <InterviewMasterClassBasicReviewsModal review={myReview} updateLecture={updateLecture} />
            ),
          })
        }
      >
        <span>강의 후기</span>
      </button>
      <button
        className='footer-button'
        onClick={() => modal.open({ element: <InterviewMasterClassBasicSelfStudyModal /> })}
      >
        <span>강의 수강 후 준비방법</span>
      </button>
    </div>
  );
}

const StyledContainer = styled.div<{ isMobile: boolean }>`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css');
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css');

  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 400px')};
  background-color: #f7f7f7;
  min-height: calc(100vh - 70px);
  font-family: 'Pretendard' !important;
  border-top: 1px solid #e8e8e8;
  border-bottom: 1px solid #e8e8e8;

  > .left {
    height: 100%;
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;

    > .header {
      background-color: #fff;
      height: 68px;
      flex-shrink: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0 ${({ isMobile }) => (isMobile ? '18px' : '32px')};
      column-gap: 8px;

      > h1 {
        margin: 0;
        font-weight: 600;
        font-size: 16px;
      }
    }

    > .main {
      flex-grow: 1;
      display: grid;
      grid-template-rows: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 56px')};

      > .player {
        position: relative;
        ${({ isMobile }) => (isMobile ? `padding-top: 56.25%;` : '')}

        > .loading-indicator-wrapper {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
          display: grid;
          place-items: center;
        }
      }
    }
  }

  > .right {
    max-height: calc(100vh - 70px);
    overflow-y: scroll;

    > .header {
      height: 68px;
      padding: 0 24px;
      display: flex;
      align-items: center;
      font-weight: 700;
      font-size: 16px;
    }

    > .lectures-list {
      padding: ${({ isMobile }) => (isMobile ? '0' : '0 24px 24px')};
    }
  }

  .footer {
    border-top: 1px solid #e8e8e8;
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    ${({ isMobile }) =>
      isMobile
        ? `
    height: 56px;
    position: sticky;
    bottom: 0;
    `
        : ''}

    background-color: #f7f7f7;

    > .footer-button {
      all: unset;
      box-sizing: border-box;
      cursor: pointer;

      > span {
        display: block;
        text-align: center;
        border-right: 1px solid #e8e8e8;
        font-weight: 500;
        font-size: 14px;
        color: #555;
        line-height: 1.2;
      }

      &:last-of-type > span {
        border: none;
      }
    }
  }
`;

export default InterviewMasterClassBasicPlayerPage;
